/* Copyright (c) 2020-present CloudIO Inc */

import {
  CommonProps,
  defaultCommonProps,
} from '../../layout/Sidebar/panels/PropertiesView/CommonProperties';
import {
  BooleanExpressionValue,
  ExpressionValue,
  GridFlex,
  PropertyDefinitions,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { gridSize } from '../../registry/constants';

export interface BaseField extends CommonProps {
  autoFocus?: boolean;
  flex?: GridFlex;
  helperText?: string;
  insertable?: BooleanExpressionValue;
  label?: ExpressionValue;
  labelColor?: ExpressionValue;
  colorExpression?: ExpressionValue;
  bgColorExpression?: ExpressionValue;
  required?: boolean;
  updatable?: BooleanExpressionValue;
  viewAttribute?: string;
}

export interface FontStyle {
  textFontSize?: number;
  textBold?: boolean;
  textItalic?: boolean;
  labelBold?: boolean;
  labelItalic?: boolean;
}

export const baseFieldDefaultValue: Required<BaseField> = {
  ...defaultCommonProps,
  autoFocus: false,
  flex: { xs: 6 },
  helperText: '',
  insertable: { value: true, type: 'boolean' },
  label: { type: 'text', value: 'Change Me!' },
  labelColor: { type: 'text', value: '' },
  colorExpression: { type: 'text', value: '' },
  bgColorExpression: { type: 'text', value: '' },
  required: false,
  updatable: { value: true, type: 'boolean' },
  viewAttribute: '',
};

export const fontStyleDefaultValue: Required<FontStyle> = {
  textFontSize: 12,
  textBold: false,
  textItalic: false,
  labelBold: false,
  labelItalic: false,
};

export const baseFieldProps: PropertyDefinitions<
  Omit<BaseField, keyof CommonProps>
> = {
  label: {
    label: 'Label',
    type: 'expression',
    returnType: async () => 'string',
  },
  viewAttribute: {
    label: 'View Attribute',
    type: 'ViewAttributeChooser',
    required: true,
    category: 'Data',
    aliasType: 'STORE',
    getAttributeDataTypes: () => ['String'],
    preventMultiEdit: true,
  } as ViewAttributeChooserProp,
  insertable: {
    label: 'Insertable',
    type: 'booleanExpression',
    category: 'Data',
  },
  updatable: {
    label: 'Updatable',
    type: 'booleanExpression',
    category: 'Data',
  },
  flex: {
    label: 'Flex',
    type: 'grid',
    options: gridSize,
    helpText:
      'Defines the number of grids the component is going to use on an extra large screen.',
    category: 'Layout',
  },
  helperText: {
    label: 'Helper Text',
    type: 'string',
  },
  required: {
    label: 'Required',
    type: 'boolean',
    helpText:
      'If checked, the label will indicate that this input is required. This is only for visual purposes. If you want to enforce this, use the required property on the datasource attribute.',
  },
  autoFocus: {
    label: 'Auto Focus',
    type: 'boolean',
    helpText: 'Check to focus on this field whenever it is rendered',
  },
  labelColor: {
    category: 'Look & Feel',
    label: 'Label Color',
    type: 'ColorExpression',
    placeholder: "e.g. '#FFFFFF'",
  },
  colorExpression: {
    category: 'Look & Feel',
    label: 'Text Color',
    type: 'ColorExpression',
    placeholder: "e.g. '#FFFFFF'",
  },
  bgColorExpression: {
    category: 'Look & Feel',
    label: 'Background Color',
    type: 'ColorExpression',
    placeholder: "e.g. '#157CEC'",
  },
};

export const fontStyleProps: PropertyDefinitions<
  Omit<FontStyle, keyof CommonProps>
> = {
  textFontSize: {
    category: 'Look & Feel',
    label: 'Text Font Size',
    type: 'numberInput',
  },
  textBold: {
    category: 'Look & Feel',
    label: 'Text Bold',
    type: 'boolean',
    helpText: 'Check to diplay value in bold',
  },
  textItalic: {
    category: 'Look & Feel',
    label: 'Text Italic',
    type: 'boolean',
    helpText: 'Check to diplay value in italic',
  },
  labelBold: {
    category: 'Look & Feel',
    label: 'Label Bold',
    type: 'boolean',
    helpText: 'Check to diplay label in bold',
  },
  labelItalic: {
    category: 'Look & Feel',
    label: 'Label Italic',
    type: 'boolean',
    helpText: 'Check to diplay label in italic',
  },
};
